/**
 * RelayLoadType capability-flag lookup — what a load type IS/DOES
 * (digital, fixture-capable, normally-closed-capable, reboot-capable, triface
 * motor, latching-pair-capable), replacing the hardcoded catalog-id literals
 * (`loadType === 4`, `=== 7 || === 8`, …) load-type KIND checks used to embed.
 *
 * Pure — no redux, no IO — so it's safe to import from domain modules that
 * must stay React/store-free (relayConfigForm.js) as well as from the
 * reference slice's selector.
 *
 * Contains:
 * - DEFAULT_LOAD_TYPE_CAPS — safe defaults for an absent/partial catalog row
 * - buildLoadTypeCapsById — RelayLoadType catalog rows -> caps map keyed by id
 * - getLoadTypeCapabilities — single loadType id -> its caps, safe-defaulted
 */

/**
 * Safe defaults for a RelayLoadType's capability flags, applied when a
 * catalog row omits a flag or a loadType id isn't found in the catalog at
 * all. Mirrors the pre-flags literal-id behavior: unknown/absent ids were
 * treated as non-digital, non-triface, non-pairing, with fixtures allowed.
 */
export const DEFAULT_LOAD_TYPE_CAPS = Object.freeze({
  is_digital: false,
  supports_light_fixtures: true,
  supports_normally_closed: false,
  supports_reboot_command: false,
  is_triface_motor: false,
  supports_pairing: false,
});

/**
 * Builds the RelayLoadType catalog's capability-flag map, keyed by id. Each
 * row is safe-defaulted per-flag so a partial catalog row still yields
 * booleans.
 *
 * @param {object[]} relayLoadTypes - the relay_load_types reference catalog
 * @returns {object} capability flags keyed by RelayLoadType id
 */
export function buildLoadTypeCapsById(relayLoadTypes) {
  return relayLoadTypes.reduce((capsById, loadType) => {
    capsById[loadType.id] = {
      is_digital: loadType.is_digital ?? DEFAULT_LOAD_TYPE_CAPS.is_digital,
      supports_light_fixtures: loadType.supports_light_fixtures ?? DEFAULT_LOAD_TYPE_CAPS.supports_light_fixtures,
      supports_normally_closed: loadType.supports_normally_closed ?? DEFAULT_LOAD_TYPE_CAPS.supports_normally_closed,
      supports_reboot_command: loadType.supports_reboot_command ?? DEFAULT_LOAD_TYPE_CAPS.supports_reboot_command,
      is_triface_motor: loadType.is_triface_motor ?? DEFAULT_LOAD_TYPE_CAPS.is_triface_motor,
      supports_pairing: loadType.supports_pairing ?? DEFAULT_LOAD_TYPE_CAPS.supports_pairing,
    };
    return capsById;
  }, {});
}

/**
 * Resolves a single loadType id's capability flags out of the map
 * `buildLoadTypeCapsById` builds, falling back to `DEFAULT_LOAD_TYPE_CAPS`
 * for an id absent from the catalog (deleted row, unset relay, etc.).
 *
 * @param {object} capsById - buildLoadTypeCapsById(relayLoadTypes) / selectRelayLoadTypeCapsById(state)
 * @param {?number} loadTypeId
 * @returns {{is_digital: boolean, supports_light_fixtures: boolean, supports_normally_closed: boolean, supports_reboot_command: boolean, is_triface_motor: boolean, supports_pairing: boolean}}
 */
export function getLoadTypeCapabilities(capsById, loadTypeId) {
  return capsById[loadTypeId] ?? DEFAULT_LOAD_TYPE_CAPS;
}
